import { useEffect, useState, type ReactNode } from 'react'
import { Box } from '@mui/material'
import { spacings, ThemeProvider, TIconButtonProps, TTypographyProps } from '@guidde/design-system'
import { PlaybookHeader } from './PlaybookHeader'
import { QuickGuiddeType } from 'app/types'
import { containsRTL } from 'modules/utils'

const Breakpoints = {
    xs: 337,
    sm: 344,
    md: 600,
    lg: 800,
}

type Styling = {
    paddings: { px: number; mb: number; pt: number }
    switcher: {
        isIconButton: boolean
        size: TIconButtonProps['size']['size']
        iconSize: number
    }
    font: {
        size: Exclude<TTypographyProps['size'], 'xxl'>
        variant: TTypographyProps['variant']
        mb: number
    }
}
const getStyling = (containerWidth: number): Styling => {
    if (containerWidth < Number(Breakpoints.md)) {
        return {
            paddings: { px: spacings['xl'], mb: spacings['2xl'], pt: spacings['3xl'] },
            switcher: {
                isIconButton: true,
                size: 'sm',
                iconSize: 12,
            },
            font: {
                size: 'sm',
                variant: 'text',
                mb: spacings['md'],
            },
        }
    }

    if (containerWidth < Number(Breakpoints.lg)) {
        return {
            paddings: { px: spacings['3xl'], mb: spacings['md'], pt: spacings['lg'] },
            switcher: {
                isIconButton: true,
                iconSize: 20,
                size: 'md',
            },
            font: {
                size: 'xl',
                variant: 'text',
                mb: spacings['lg'],
            },
        }
    }

    return {
        paddings: { px: spacings['6xl'], mb: spacings['3xl'], pt: spacings['lg'] },
        switcher: {
            isIconButton: false,
            size: 'sm',
            iconSize: 20,
        },
        font: {
            size: 'sm',
            variant: 'heading',
            mb: spacings['xl'],
        },
    }
}

type Props = { children: ReactNode; playbook: QuickGuiddeType; onLanguageChange: (_: string) => void }

export const ContentWrapper = ({ children, playbook, onLanguageChange }: Props) => {
    const [styling, setStyling] = useState(getStyling(window.innerWidth))

    useEffect(() => {
        const handleResize = () => {
            setStyling(getStyling(document.body.clientWidth))
        }
        window.addEventListener('resize', handleResize)

        return () => window.removeEventListener('resize', handleResize)
    }, [setStyling])

    const isRtl = containsRTL(playbook.language?.langCode || '')

    return (
        <ThemeProvider>
            <Box
                display="flex"
                flexDirection="column"
                maxWidth={window.innerWidth}
                sx={styling.paddings}
            >
                <PlaybookHeader
                    titleSize={styling.font.size}
                    titleVariant={styling.font.variant}
                    isRtl={isRtl}
                    title={playbook.title}
                    onLanguageChange={onLanguageChange}
                    buttonStyling={styling.switcher}
                    playbook={playbook}
                />
                <Box sx={{ mb: styling.font.mb }} />
                {children}
            </Box>
        </ThemeProvider>
    )
}
